// Core vocabularies shared by the server, the web app and the pure packages.
// Source of truth: integration plan chapter 6 (6.2.3 event kinds, 6.3 common columns and value states,
// 6.3.3 reason keys, 6.3.4 record levels). Changing a list here is a contract change: bump consumers' tests.
import { z } from 'zod';

/** Collection channel (plan 5.1). C5 = the app's own records (audit, consent), never usage rows. */
export const CHANNELS = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'] as const;
export const Channel = z.enum(CHANNELS);
export type Channel = z.infer<typeof Channel>;

/** Usage method + tool. `cli.*` = CLI tool card, `api.*` = API key call card (plan 5.3). */
export const SOURCES = ['cli.claude_code', 'cli.codex', 'api.anthropic', 'api.openai'] as const;
export const Source = z.enum(SOURCES);
export type Source = z.infer<typeof Source>;

/** Usage method as shown on screen: CLI tool vs API key call (REQ-2.6). */
export const METHODS = ['cli', 'api'] as const;
export const Method = z.enum(METHODS);
export type Method = z.infer<typeof Method>;
export const methodOf = (source: Source): Method => (source.startsWith('cli.') ? 'cli' : 'api');

/** Where the CLI ran (Claude `app.entrypoint`, Codex `originator`). */
export const SURFACES = ['terminal', 'ide', 'desktop', 'sdk', 'automation', 'unknown'] as const;
export const Surface = z.enum(SURFACES);
export type Surface = z.infer<typeof Surface>;

/** Path the record took before reaching the server. */
export const VIAS = ['direct', 'gateway', 'helper'] as const;
export const Via = z.enum(VIAS);
export type Via = z.infer<typeof Via>;

/** Billing method of a CLI tool use (not of API calls). */
export const AUTH_METHODS = ['subscription', 'api_key', 'unknown'] as const;
export const AuthMethod = z.enum(AUTH_METHODS);
export type AuthMethod = z.infer<typeof AuthMethod>;

export const DEDUP_STATES = ['none', 'pending', 'matched', 'unmatched', 'unverified'] as const;
export const DedupState = z.enum(DEDUP_STATES);
export type DedupState = z.infer<typeof DedupState>;

export const LINK_CONFIDENCES = ['exact', 'estimated'] as const;
export const LinkConfidence = z.enum(LINK_CONFIDENCES);
export type LinkConfidence = z.infer<typeof LinkConfidence>;

/** Normalized event kinds stored in `events.kind` (plan 6.2.3, 13 kinds). Metrics live in `metric_points`. */
export const EVENT_KINDS = [
  'session.start',
  'user.prompt',
  'user.command',
  'assistant.output',
  'api.request',
  'api.usage',
  'api.error',
  'api.refusal',
  'tool.decision',
  'tool.result',
  'cost.estimate',
  'misc',
  'gateway.call',
] as const;
export const EventKind = z.enum(EVENT_KINDS);
export type EventKind = z.infer<typeof EventKind>;

/** Per-value storage state (plan 6.3.2). 0 is a value (`available`), never "no record". */
export const VALUE_STATES = [
  'available',
  'not_sent',
  'disabled',
  'estimated',
  'truncated',
  'unsupported',
  'expired',
  'consent_off',
] as const;
export const ValueState = z.enum(VALUE_STATES);
export type ValueState = z.infer<typeof ValueState>;

/** The only 13 reason keys a screen may show for "기록 없음 · 이유" (plan 6.3.3, 8.5.3). */
export const MISSING_REASONS = [
  'consent_before',
  'paused',
  'level_off',
  'cli_not_sent',
  'tool_not_sent',
  'not_applicable',
  'version_checking',
  'old_version',
  'expired',
  'gap',
  'permission',
  'small_group',
  'verifying',
] as const;
export const MissingReason = z.enum(MISSING_REASONS);
export type MissingReason = z.infer<typeof MissingReason>;

/** Consent items (plan 6.3.4, 11.3.1). Record levels ①~④ are cumulative sets of the first four. */
export const CONSENT_ITEMS = ['basic', 'prompt', 'answer', 'work_detail', 'tool_output', 'server_upload'] as const;
export const ConsentItem = z.enum(CONSENT_ITEMS);
export type ConsentItem = z.infer<typeof ConsentItem>;

export const CONSENT_ACTIONS = ['grant', 'change', 'pause', 'resume', 'withdraw'] as const;
export const ConsentAction = z.enum(CONSENT_ACTIONS);
export type ConsentAction = z.infer<typeof ConsentAction>;

/** Record level 1..4: ① basic ② +prompt ③ +answer ④ +work detail. */
export const RECORD_LEVELS = [1, 2, 3, 4] as const;
export const RecordLevel = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export type RecordLevel = z.infer<typeof RecordLevel>;
export const RECORD_LEVEL_ITEMS: Record<RecordLevel, readonly ConsentItem[]> = {
  1: ['basic'],
  2: ['basic', 'prompt'],
  3: ['basic', 'prompt', 'answer'],
  4: ['basic', 'prompt', 'answer', 'work_detail'],
};

/** Visibility scope of a permission key (plan 7.2.1). */
export const SCOPES = ['self', 'team', 'all'] as const;
export const Scope = z.enum(SCOPES);
export type Scope = z.infer<typeof Scope>;

/** System roles: 3 base roles + 5 presets (plan 7.1.2). Custom roles use their own keys. */
export const BASE_ROLES = ['admin', 'manager', 'member'] as const;
export const PRESET_ROLES = ['content_reviewer', 'log_reviewer', 'ops', 'stats_viewer', 'delegated_admin'] as const;
export const SYSTEM_ROLES = [...BASE_ROLES, ...PRESET_ROLES] as const;
export const SystemRole = z.enum(SYSTEM_ROLES);
export type SystemRole = z.infer<typeof SystemRole>;

/** Stored text kinds in `contents.role`. */
export const CONTENT_ROLES = ['prompt', 'response', 'tool_input', 'tool_output'] as const;
export const ContentRole = z.enum(CONTENT_ROLES);
export type ContentRole = z.infer<typeof ContentRole>;

export const REQUEST_STATUSES = ['ok', 'error', 'refused', 'interrupted'] as const;
export const RequestStatus = z.enum(REQUEST_STATUSES);
export type RequestStatus = z.infer<typeof RequestStatus>;

export const TOOL_DECISIONS = ['accept', 'reject', 'abort', 'timed_out'] as const;
export const ToolDecision = z.enum(TOOL_DECISIONS);
export type ToolDecision = z.infer<typeof ToolDecision>;

export const DECISION_SOURCES = ['user', 'config', 'hook'] as const;
export const DecisionSource = z.enum(DECISION_SOURCES);
export type DecisionSource = z.infer<typeof DecisionSource>;

/** Connection token kinds and their fixed prefixes (D-005). The prefixes are registered in redaction and secret scanning. */
export const TOKEN_KINDS = ['collect', 'gateway', 'service'] as const;
export const TokenKind = z.enum(TOKEN_KINDS);
export type TokenKind = z.infer<typeof TokenKind>;
export const TOKEN_PREFIX: Record<TokenKind, string> = { collect: 'axh_ing_', gateway: 'axh_gw_', service: 'axh_svc_' };
/** Header carrying the collection token on /otlp/v1/* (D-005). */
export const COLLECT_TOKEN_HEADER = 'x-aiax-hub-token';
/** Old PoC headers: requests with them are refused (401) but counted as "old settings" (plan 4.8). */
export const LEGACY_TOKEN_HEADERS = ['x-ai-trace-token', 'x-local-otel-token'] as const;

// ───────────── Display vocabularies (plan 8.5, 9.2, 10.x) — added for the API contract ─────────────

/** How a single number/cell is drawn (plan 8.5.2): value · confirmed zero · "no record · reason" · "partial · reason". */
export const DISPLAY_STATES = ['value', 'zero', 'none', 'partial'] as const;
export const DisplayState = z.enum(DISPLAY_STATES);
export type DisplayState = z.infer<typeof DisplayState>;

/** The only 5 "partial" reasons (plan 8.5.4). `verifying` is shared with MISSING_REASONS. */
export const PARTIAL_REASONS = ['truncated', 'estimated_link', 'estimated', 'unofficial', 'verifying'] as const;
export const PartialReason = z.enum(PARTIAL_REASONS);
export type PartialReason = z.infer<typeof PartialReason>;

/**
 * Tool-specific help keys that refine a reason (web i18n `reasonDetail.<key>`), e.g. `tool_not_sent` + `codex_active`
 * = "Codex doesn't send active time". The server only sends the key.
 */
export const REASON_DETAILS = [
  'codex_active',
  'claude_reasoning',
  'anthropic_reasoning',
  'codex_loc',
  'openai_cache_write',
  'codex_cache_write',
  'codex_cost',
  'codex_slash',
  'codex_tokens',
  'codex_answers',
  'api_time',
  'api_duration',
  'api_files',
  'metrics_off',
  /** api-reconcile cells: no gateway cost for that day/model · not in the org report · nothing to compare. */
  'no_gateway',
  'no_org',
  'no_compare',
  /** SCR-15: model missing from the price table, so no price-table cost was computed. */
  'price_missing',
] as const;
export const ReasonDetail = z.enum(REASON_DETAILS);
export type ReasonDetail = z.infer<typeof ReasonDetail>;

/** Tool ids used in URLs (`tool=`) and in the support table (plan 10.3.5, 5.9). Map 1:1 to SOURCES. */
export const TOOLS = ['claude_code', 'codex', 'anthropic_api', 'openai_api'] as const;
export const Tool = z.enum(TOOLS);
export type Tool = z.infer<typeof Tool>;
export const TOOL_OF_SOURCE: Record<Source, Tool> = {
  'cli.claude_code': 'claude_code',
  'cli.codex': 'codex',
  'api.anthropic': 'anthropic_api',
  'api.openai': 'openai_api',
};
export const SOURCE_OF_TOOL: Record<Tool, Source> = {
  claude_code: 'cli.claude_code',
  codex: 'cli.codex',
  anthropic_api: 'api.anthropic',
  openai_api: 'api.openai',
};

/** AI companies behind the tools. */
export const PROVIDERS = ['anthropic', 'openai'] as const;
export const Provider = z.enum(PROVIDERS);
export type Provider = z.infer<typeof Provider>;

/** Support-table items (plan 5.9, SCR-08 support tab). A badge links to `#/connect?tab=support&cell=support-<tool>-<item>`. */
export const SUPPORT_ITEMS = ['prompt', 'answer', 'tool_detail', 'active_time', 'lines', 'tokens', 'reasoning', 'cache_write', 'cost'] as const;
export const SupportItem = z.enum(SUPPORT_ITEMS);
export type SupportItem = z.infer<typeof SupportItem>;
/** Evidence level of a support-table cell (BUILD-SPEC-data 0.2 `EvidenceLevel`, cli_support_matrix). */
export const EVIDENCE_LEVELS = ['verified', 'source', 'doc'] as const;
export const EvidenceLevel = z.enum(EVIDENCE_LEVELS);
export type EvidenceLevel = z.infer<typeof EvidenceLevel>;
/** Availability of a support-table cell (BUILD-SPEC-data 0.2 `Availability`). */
export const AVAILABILITIES = ['supported', 'conditional', 'unofficial', 'unsupported', 'na', 'checking'] as const;
export const Availability = z.enum(AVAILABILITIES);
export type Availability = z.infer<typeof Availability>;

/** Reasoning effort buckets (plan 10.1.5). `unknown` = not reported; API calls use `not_applicable` in groupings. */
export const EFFORTS = ['low', 'medium', 'high', 'max', 'unknown'] as const;
export const Effort = z.enum(EFFORTS);
export type Effort = z.infer<typeof Effort>;

/**
 * Who decided a tool use, as grouped on screen (plan 10.2.7): the stored `DecisionSource` (user · config · hook)
 * plus `none` for "no actor recorded" (tool_calls.decision_source null).
 */
export const DECISION_ACTORS = [...DECISION_SOURCES, 'none'] as const;
export const DecisionActor = z.enum(DECISION_ACTORS);
export type DecisionActor = z.infer<typeof DecisionActor>;

/** Request failure classes (BUILD-SPEC-data 0.2 `ErrorType`, requests.error_type). */
export const ERROR_TYPES = ['rate_limit', 'server_error', 'auth', 'client_error', 'network', 'timeout', 'refusal', 'interrupted', 'policy_rejected'] as const;
export const ErrorType = z.enum(ERROR_TYPES);
export type ErrorType = z.infer<typeof ErrorType>;

/** The 12 tool-action groups (plan 9.2 작업 묶음 12종; BUILD-SPEC-data 0.2 `ToolGroup`, tool_calls.tool_group). */
export const TOOL_GROUPS = ['read', 'edit', 'write', 'command', 'search', 'find', 'web', 'delegate', 'plan', 'skill', 'mcp', 'other'] as const;
export const ToolGroup = z.enum(TOOL_GROUPS);
export type ToolGroup = z.infer<typeof ToolGroup>;

/** File action kinds as stored (BUILD-SPEC-data 0.2 `FileKind`, plan 6.8): Claude read/edit/write, Codex add/update/delete. */
export const FILE_KINDS = ['read', 'edit', 'write', 'add', 'update', 'delete'] as const;
export const FileKind = z.enum(FILE_KINDS);
export type FileKind = z.infer<typeof FileKind>;

/**
 * The 4 cost kinds that are never summed together (plan 6.4.4, 10.1.2; BUILD-SPEC-data 0.2 `CostKind`):
 * CLI estimate · Codex unofficial · gateway price table · org bill.
 */
export const COST_KINDS = ['cli_estimate', 'unofficial', 'price_table', 'billed'] as const;
export const CostKind = z.enum(COST_KINDS);
export type CostKind = z.infer<typeof CostKind>;

/** Content kept with a gateway/collect token (BUILD-SPEC-data 0.2 `ContentPolicy`): basic info only · last Q&A text. */
export const CONTENT_POLICIES = ['meta', 'text'] as const;
export const ContentPolicy = z.enum(CONTENT_POLICIES);
export type ContentPolicy = z.infer<typeof ContentPolicy>;

/** Billing method as a grouping (CLI only; API calls are `not_applicable`). */
export const AUTH_MODE_GROUPS = ['subscription', 'api_key', 'unknown', 'not_applicable'] as const;
export const AuthModeGroup = z.enum(AUTH_MODE_GROUPS);
export type AuthModeGroup = z.infer<typeof AuthModeGroup>;

/** Reason categories for opening someone else's text (plan 7.5.1). Fixed: no evaluation/attendance category. */
export const REASON_CATEGORIES = ['security_incident', 'user_support', 'outage', 'other'] as const;
export const ReasonCategory = z.enum(REASON_CATEGORIES);
export type ReasonCategory = z.infer<typeof ReasonCategory>;

/** Device/tool connection state (plan 8.4.8): ok ≤4h · quiet 4h~3d · down >3d · revoked. */
export const CONNECTION_STATUSES = ['ok', 'quiet', 'down', 'revoked'] as const;
export const ConnectionStatus = z.enum(CONNECTION_STATUSES);
export type ConnectionStatus = z.infer<typeof ConnectionStatus>;

/** CLI version state against the support table (plan 5.9). */
export const VERSION_STATES = ['supported', 'checking', 'old'] as const;
export const VersionState = z.enum(VERSION_STATES);
export type VersionState = z.infer<typeof VersionState>;

/** Record-status badges on the list and detail header (plan 8.5.4). */
export const RECORD_BADGES = ['received', 'level_off', 'cli_off', 'redacted', 'partial', 'unsupported_ok', 'estimated_link', 'tamper_suspected'] as const;
export const RecordBadge = z.enum(RECORD_BADGES);
export type RecordBadge = z.infer<typeof RecordBadge>;

/** Consent state line on "My records" (plan 11.3.4). */
export const CONSENT_STATES = ['before', 'recording', 'paused', 'reconsent_required', 'withdrawn'] as const;
export const ConsentState = z.enum(CONSENT_STATES);
export type ConsentState = z.infer<typeof ConsentState>;

/** Audit kinds = filter chips of SCR-13 (plan 8.4.13, mockup D.AUDIT_KINDS). */
export const AUDIT_KINDS = ['login', 'permission', 'policy', 'content_view', 'person_view', 'export', 'consent', 'deletion', 'legal_hold', 'key_rotation', 'glossary', 'other'] as const;
export const AuditKind = z.enum(AUDIT_KINDS);
export type AuditKind = z.infer<typeof AuditKind>;
/**
 * Audit row result (BUILD-SPEC-data 0.2 `AuditResult`: success · denied · failure) plus `pending` for a request that
 * waits for a 2-person approval (approval.request rows; mockup).
 */
export const AUDIT_RESULTS = ['success', 'denied', 'failure', 'pending'] as const;
export const AuditResult = z.enum(AUDIT_RESULTS);
export type AuditResult = z.infer<typeof AuditResult>;
/** Audit retention classes (plan 11.8.2; BUILD-SPEC-data 0.2 `RetentionClass`): permission 3y · access 2y · general 1y. */
export const RETENTION_CLASSES = ['permission_3y', 'access_2y', 'general_1y'] as const;
export const RetentionClass = z.enum(RETENTION_CLASSES);
export type RetentionClass = z.infer<typeof RetentionClass>;

/**
 * Deletion request scope/status (plan 11.9.2; BUILD-SPEC-data 0.2 `DeletionScope`/`DeletionStatus`). `blocked` = the
 * request is in: hidden from every role, waiting to be processed. `canceled` (the person took it back, e.g. undoing
 * a withdrawal) is a contract addition to the data vocabulary.
 */
export const DELETION_SCOPES = ['session', 'range', 'all'] as const;
export const DeletionScope = z.enum(DELETION_SCOPES);
export type DeletionScope = z.infer<typeof DeletionScope>;
export const DELETION_STATUSES = ['blocked', 'deleted', 'held', 'rejected', 'canceled'] as const;
export const DeletionStatus = z.enum(DELETION_STATUSES);
export type DeletionStatus = z.infer<typeof DeletionStatus>;

/** 2-person approvals and investigation cases (BUILD-SPEC-data 0.2 `ApprovalKind`/`ApprovalStatus`/`CaseStatus`). */
export const APPROVAL_KINDS = ['role_definition', 'role_grant', 'manager_team_content', 'investigation_case', 'notify_deferral'] as const;
export const ApprovalKind = z.enum(APPROVAL_KINDS);
export type ApprovalKind = z.infer<typeof ApprovalKind>;
export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected', 'withdrawn', 'expired'] as const;
export const ApprovalStatus = z.enum(APPROVAL_STATUSES);
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;
export const CASE_STATUSES = ['pending', 'approved', 'rejected', 'closed'] as const;
export const CaseStatus = z.enum(CASE_STATUSES);
export type CaseStatus = z.infer<typeof CaseStatus>;

/** Cleanup/sync run result (BUILD-SPEC-data 0.2 `JobStatus`; retention_jobs, connector_runs). */
export const JOB_STATUSES = ['running', 'ok', 'partial', 'error', 'skipped'] as const;
export const JobStatus = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatus>;

/** Glossary categories (plan 9.5.3) and external link status (plan 9.5.8). */
export const GLOSSARY_CATEGORIES = ['ai', 'usage', 'logging', 'security', 'access', 'dev', 'stats', 'system'] as const;
export const GlossaryCategory = z.enum(GLOSSARY_CATEGORIES);
export type GlossaryCategory = z.infer<typeof GlossaryCategory>;
export const LINK_STATUSES = ['ok', 'check_needed', 'moved', 'pending'] as const;
export const LinkStatus = z.enum(LINK_STATUSES);
export type LinkStatus = z.infer<typeof LinkStatus>;

/** Redaction methods (plan 11.6): erase · consistent token (per conversation) · marker · omission marker · not stored. */
export const REDACTION_METHODS = ['erase', 'token', 'marker', 'omit', 'not_stored'] as const;
export const RedactionMethod = z.enum(REDACTION_METHODS);
export type RedactionMethod = z.infer<typeof RedactionMethod>;

/** Notification kinds (plan 8.2.2: 9 defined + spike/team/policy/gap/link/version extras seen in the mockup). */
export const NOTIFICATION_KINDS = [
  'device_down',
  'collection_gap',
  'collection_unknown',
  'reconsent',
  'record_viewed',
  'token_expiring',
  'budget_warning',
  'version_checking',
  'bulk_view',
  'link_broken',
  'usage_spike',
  'team_update',
  'policy_changed',
  'deletion_processed',
  'approval_pending',
  'grant_expiring',
  'grant_dormant',
  'grant_expiry_deferred',
  'audit_summary',
] as const;
export const NotificationKind = z.enum(NOTIFICATION_KINDS);
export type NotificationKind = z.infer<typeof NotificationKind>;
export const TONES = ['info', 'ok', 'warn', 'danger'] as const;
export const Tone = z.enum(TONES);
export type Tone = z.infer<typeof Tone>;

/** Operating systems offered by the connection wizard (plan 8.4.8 ④). */
/** Setup snippet flavour: macOS and Linux share one (`unix`: sh + ~/ paths), Windows differs (PowerShell + %USERPROFILE%). */
export const SETUP_OS = ['unix', 'windows'] as const;
export const SetupOs = z.enum(SETUP_OS);
export type SetupOs = z.infer<typeof SetupOs>;

/** Login methods of a person account (plan 11.4.1). */
export const AUTH_PROVIDERS = ['local', 'google'] as const;
export const AuthProvider = z.enum(AUTH_PROVIDERS);
export type AuthProvider = z.infer<typeof AuthProvider>;

/**
 * Kinds inside a redaction marker `{{redact:<kind>}}` left in stored text (plan 11.6.2). The web draws
 * "▇▇▇ 가림: <kind>" from the marker; the server never returns the original.
 */
export const REDACTION_KINDS = ['secret', 'credential', 'rrn', 'email', 'phone', 'card', 'account', 'blob', 'path', 'name'] as const;
export const RedactionKind = z.enum(REDACTION_KINDS);
export type RedactionKind = z.infer<typeof RedactionKind>;
export const REDACTION_MARKER = /\{\{redact:([a-z_]+)\}\}/g;

/** Execution surface filter value: SURFACES + `not_applicable` for API key calls. */
export const SURFACE_FILTERS = [...SURFACES, 'not_applicable'] as const;
export const SurfaceFilter = z.enum(SURFACE_FILTERS);
export type SurfaceFilter = z.infer<typeof SurfaceFilter>;

/** Why a device/tool connection is unhealthy (replaces the mockup's free-text `reason`). */
export const CONNECTION_ISSUES = [
  'no_records',
  'helper_not_responding',
  'helper_backlog',
  'collect_token_expired',
  'collect_token_revoked',
  'legacy_settings',
  'sends_more_than_consent',
  'version_old',
  'version_checking',
] as const;
export const ConnectionIssueKey = z.enum(CONNECTION_ISSUES);
export type ConnectionIssueKey = z.infer<typeof ConnectionIssueKey>;

/** Why intake stopped for a while (BUILD-SPEC-data 0.2 `GapReason`, collection_gaps.reason; replaces mockup free text). */
export const GAP_REASONS = ['server_unreachable', 'no_receipt', 'helper_silent', 'restore', 'maintenance'] as const;
export const GapReason = z.enum(GAP_REASONS);
export type GapReason = z.infer<typeof GapReason>;

/** Records dropped by the collector, counted per reason and never stored (BUILD-SPEC-data 0.2 `DropReason`, plan 5.10). */
export const DROP_REASONS = [
  'no_consent',
  'before_consent',
  'notice_outdated',
  'withdrawn',
  'paused',
  'inside_pause_window',
  'store_off',
  'ingest_disabled',
  'undeterminable_time',
  'older_than_retention',
  'parse_error',
  'no_session',
  'unknown_event',
  'excluded_by_policy',
  'noise',
  'level_off',
] as const;
export const DropReason = z.enum(DROP_REASONS);
export type DropReason = z.infer<typeof DropReason>;

/** Requests the collector refused before reading them (BUILD-SPEC-data 0.2 `RejectReason`). */
export const REJECT_REASONS = ['no_token', 'bad_token', 'revoked', 'expired', 'legacy_header', 'browser_origin', 'rate_limited', 'busy', 'too_large', 'bad_format'] as const;
export const RejectReason = z.enum(REJECT_REASONS);
export type RejectReason = z.infer<typeof RejectReason>;

/** Saved-view sharing (BUILD-SPEC-data 0.2 `SavedViewScope`): only me · anyone with the link (re-judged per viewer). */
export const SAVED_VIEW_SCOPES = ['private', 'link'] as const;
export const SavedViewScope = z.enum(SAVED_VIEW_SCOPES);
export type SavedViewScope = z.infer<typeof SavedViewScope>;

/** Provider credential scope (BUILD-SPEC-data 0.2 `CredentialScope`) and purpose (`CredentialPurpose`). */
export const CREDENTIAL_SCOPES = ['org', 'team', 'user'] as const;
export const CredentialScope = z.enum(CREDENTIAL_SCOPES);
export const CREDENTIAL_PURPOSES = ['gateway', 'admin'] as const;
export const CredentialPurpose = z.enum(CREDENTIAL_PURPOSES);
